import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { AppConfig } from '../config/types';
import { initPlatformApi } from '../api/initPlatformApi';
import { AuthProvider } from '../auth/AuthProvider';
import { registerAuthQueryClient } from '../auth/sessionCleanup';

export interface PlatformProviderProps {
  config: AppConfig;
  children: ReactNode;
  logoSrc?: string;
  queryClient?: QueryClient;
}

const defaultQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

export function PlatformProvider({
  config,
  children,
  logoSrc,
  queryClient: queryClientProp,
}: PlatformProviderProps) {
  const queryClient = useMemo(
    () => queryClientProp ?? defaultQueryClient,
    [queryClientProp],
  );

  // Must run synchronously before children render (AuthProvider reads config on first paint).
  const initializedConfig = useRef<AppConfig | null>(null);
  if (initializedConfig.current !== config) {
    initPlatformApi(config);
    initializedConfig.current = config;
  }

  useEffect(() => {
    registerAuthQueryClient(queryClient);
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {logoSrc ? (
        <AuthProvider logoSrc={logoSrc}>{children}</AuthProvider>
      ) : (
        <AuthProvider>{children}</AuthProvider>
      )}
    </QueryClientProvider>
  );
}
